import { FieldValues, Path, PathValue, useController, UseControllerProps } from 'react-hook-form'
import { TextField, TextFieldProps } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { ParseKeys } from 'i18next'

export type HookFormTextFieldProps<T extends FieldValues> = Omit<
  TextFieldProps,
  'name' | 'defaultValue'
> & {
  name: Path<T>
  defaultValue?: PathValue<T, Path<T>>
  control?: UseControllerProps<T>['control']
}

export function HookFormTextField<T extends FieldValues>({
  name,
  defaultValue,
  control,
  ...props
}: HookFormTextFieldProps<T>) {
  const { t } = useTranslation()
  const {
    field,
    fieldState: { error },
  } = useController<T>({ name, defaultValue, control })

  return (
    <TextField
      {...props}
      {...field}
      disabled={props.disabled}
      error={!!error}
      helperText={
        error?.message
          ? // ParseKeys is `string` in this package's standalone tsconfig (so the cast reads as
            // redundant here) but resolves to a strict key union under the consuming app's
            // i18next augmentation, where the cast is required for `tsc --noEmit` to pass.
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
            t(error.message as ParseKeys)
          : props.helperText
      }
    />
  )
}
